import type { Cell, TableColumnType, TablePayload } from '../../types';
import { formatMoney, formatNumber } from '../money.ts';

export type Column = TablePayload['columns'][number];
export type Row = Record<string, Cell>;
export type SortState = { key: string; direction: 'asc' | 'desc' } | null;
export type Density = 'comfortable' | 'compact';
export const DEFAULT_MONEY_CURRENCY = 'EUR';

/** The declared type; JSON numbers in an untyped column still read as numbers. */
export function columnType(column: Column, rows: readonly Row[]): TableColumnType {
  if (column.type) return column.type;
  const sample = rows.find((row) => row[column.key] !== null && row[column.key] !== undefined);
  return typeof sample?.[column.key] === 'number' ? 'number' : 'text';
}

export const isNumeric = (type: TableColumnType) =>
  type === 'number' || type === 'money' || type === 'percent';

/** A value the source did not provide is null: it is never shown as 0. */
export const isMissing = (value: Cell | undefined) =>
  value === null || value === undefined || value === '';

/** A declared precision fixes the decimals; otherwise up to `fallback` (Intl's default when unset). */
const fixed = (precision: number | undefined, fallback?: number) =>
  precision !== undefined
    ? { minimumFractionDigits: precision, maximumFractionDigits: precision }
    : fallback === undefined
      ? {}
      : { maximumFractionDigits: fallback };

export function formatCell(
  value: Cell | undefined,
  column: Column,
  type: TableColumnType,
  locale?: string,
  now = new Date(),
): string {
  const text = formatPlain(value, column, type, locale, now);
  // A change column states the direction of growth: a leading + (the minus is already true).
  return column.signed && typeof value === 'number' && value > 0 && text ? `+${text}` : text;
}

function formatPlain(
  value: Cell | undefined,
  column: Column,
  type: TableColumnType,
  locale?: string,
  now = new Date(),
): string {
  if (isMissing(value)) return '';
  if (type === 'money' && typeof value === 'number')
    return formatMoney(value, locale ?? 'en', column.currency ?? DEFAULT_MONEY_CURRENCY, {
      full: true,
      precision: column.precision,
    });
  if (type === 'percent' && typeof value === 'number')
    return formatNumber(value, locale ?? 'en', {
      style: 'percent',
      ...fixed(column.precision, 1),
    });
  if (type === 'number' && typeof value === 'number')
    return formatNumber(value, locale ?? 'en', fixed(column.precision));
  if (type === 'date') {
    const date = new Date(String(value));
    if (!Number.isFinite(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    }).format(date);
  }
  if (type === 'link') {
    try {
      return new URL(String(value)).hostname;
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function compareCells(a: Cell | undefined, b: Cell | undefined, type: TableColumnType) {
  if (isNumeric(type) && typeof a === 'number' && typeof b === 'number') return a - b;
  if (type === 'date') {
    const left = Date.parse(String(a));
    const right = Date.parse(String(b));
    if (Number.isFinite(left) && Number.isFinite(right)) return left - right;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Sorts a copy. Missing values always come last, in either direction. */
export function sortRows(
  rows: readonly Row[],
  column: Column | undefined,
  type: TableColumnType,
  direction: 'asc' | 'desc',
): Row[] {
  if (!column) return [...rows];
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = a[column.key];
    const right = b[column.key];
    if (isMissing(left) || isMissing(right))
      return Number(isMissing(left)) - Number(isMissing(right));
    return sign * compareCells(left, right, type);
  });
}

export function searchRows(rows: readonly Row[], columns: readonly Column[], query: string) {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) =>
    columns.some((column) =>
      String(row[column.key] ?? '')
        .toLocaleLowerCase()
        .includes(needle),
    ),
  );
}

/** Row filters: column key → the values kept. An empty set keeps every row. */
export type Filters = Record<string, string[]>;
export const filterCount = (filters: Filters) =>
  Object.values(filters).filter((values) => values.length).length;

export function filterRows(rows: readonly Row[], filters: Filters) {
  const active = Object.entries(filters).filter(([, values]) => values.length);
  return active.length
    ? rows.filter((row) => active.every(([key, values]) => values.includes(String(row[key] ?? ''))))
    : [...rows];
}

/** Columns worth filtering by: a few distinct values, never free text. */
export function filterableValues(column: Column, type: TableColumnType, rows: readonly Row[]) {
  if (!['status', 'text', 'identifier'].includes(type) || type === 'identifier') return null;
  const values = [...new Set(rows.map((row) => row[column.key]).filter((v) => !isMissing(v)))].map(
    String,
  );
  return values.length >= 2 && values.length <= 12 ? values.sort() : null;
}

export function rowId(row: Row, index: number, rowKey?: string) {
  const value = rowKey ? row[rowKey] : undefined;
  return isMissing(value) ? `#${index}` : String(value);
}

/** Rows in a window of a virtualised body: fixed row height, no pager. */
export function windowRows(total: number, rowHeight: number, scrollTop: number, viewport: number) {
  const overscan = 8;
  const top = Math.min(Math.max(0, scrollTop), Math.max(0, (total - 1) * rowHeight));
  const start = Math.max(0, Math.floor(top / rowHeight) - overscan);
  const end = Math.min(total, Math.ceil((top + viewport) / rowHeight) + overscan);
  return { start, end, before: start * rowHeight, after: Math.max(0, (total - end) * rowHeight) };
}

export const VIRTUALISE_ABOVE = 200;
export const defaultDensity = (rowCount: number): Density =>
  rowCount > 50 ? 'compact' : 'comfortable';
export const rowHeight = (density: Density) => (density === 'compact' ? 36 : 40);

/**
 * Which columns head the row detail. A declared `titleKey` wins; otherwise the
 * first text column that is not the id. The id is shown in mono only when
 * declared (`idKey`) or typed as an identifier. Every other column is the body.
 */
export function detailColumns(table: TablePayload, types: readonly TableColumnType[]) {
  const keys = table.columns.map((column) => column.key);
  const idIndex = table.idKey
    ? keys.indexOf(table.idKey)
    : types.findIndex((type) => type === 'identifier');
  let titleIndex = table.titleKey ? keys.indexOf(table.titleKey) : -1;
  if (titleIndex < 0)
    titleIndex = types.findIndex((type, index) => type === 'text' && index !== idIndex);
  if (titleIndex < 0) titleIndex = keys.findIndex((_, index) => index !== idIndex);
  const rest = keys.map((_, index) => index).filter((i) => i !== idIndex && i !== titleIndex);
  return { idIndex, titleIndex, rest };
}
